import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Divider, DrawerBody, DrawerFooter, Flex, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import InputLabel from '../../../../components/form/InputLabel';
import RequirementTemplateDropdownMenu from '../../../../components/RequirementTemplateDropdownMenu';
import { analyticsIds } from '../../../../constants/analyticsIds';
import { CypressIds } from '../../../../constants/cypressIds';
import { JobProfilePage } from '../../../../constants/duskIds';
import {
  bulkCreateInsuranceRequirements,
  resetAddInsuredToProject,
  selectCompanyInsuredsList,
  selectCurrentJob,
  selectCurrentlyAddingInsuredToJob,
  selectCurrentlyCreatingInsured,
  updateCompanyInsuredsTab
} from '../../../../redux/modules/jobs';
import { updateTrayProps } from '../../../../redux/modules/tray';
import { useRequirementTemplateData } from '../../../../services/requirement-template-service';
import colors from '../../../../styles/theme/foundation/colors';
import useUploadContractFlow from '../../../TemplatesListPage/components/modals/useUploadContractFlow';

import { multiSelectInsuredsOptions } from './MultiSelectInsureds/util';
import AddNewInsuredForm from './AddNewInsuredForm';
import MultiSelectInsureds from './MultiSelectInsureds';

const AddInsuredsToProjectTray = ({ companyId }) => {
  const dispatch = useDispatch();
  const { currentTab, selected } = useSelector(selectCompanyInsuredsList);
  const currentJob = useSelector(selectCurrentJob);
  const currentlyAddingInsuredToProject = useSelector(selectCurrentlyAddingInsuredToJob);
  const currentlyCreatingInsured = useSelector(selectCurrentlyCreatingInsured);

  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const onUploadContract = useUploadContractFlow(companyId, setSelectedTemplate);

  // Pre-select the project's default requirement template in the dropdown
  const { data: jobDefaultTemplate } = useRequirementTemplateData(
    currentJob?.defaultRequirementTemplateId,
    { enabled: !!currentJob?.defaultRequirementTemplateId }
  );

  useEffect(() => setSelectedTemplate(jobDefaultTemplate ?? null), [jobDefaultTemplate]);

  useEffect(() => () => dispatch(resetAddInsuredToProject()), [dispatch]);

  useEffect(() => {
    if (selected.length) {
      dispatch(updateTrayProps({ formIsDirty: true }));
    } else {
      dispatch(updateTrayProps({ formIsDirty: false }));
    }
  }, [selected.length]);

  const handleAddInsuredsToProject = async () => {
    const insuredIds = selected.map(insured => insured.insuredId);
    const jobRequirementTemplateId = selectedTemplate?.id ?? null;

    dispatch(bulkCreateInsuranceRequirements(
      currentJob.jobId,
      insuredIds,
      jobRequirementTemplateId
    ));
  };

  const renderAddingComponent = () => {
    if (currentTab === multiSelectInsuredsOptions.search.key) {
      return (<MultiSelectInsureds />);
    }

    return (<AddNewInsuredForm companyId={companyId} />);
  };

  const renderSubmitButton = () => {
    if (currentTab === multiSelectInsuredsOptions.search.key) {
      return (
        <>
          <Divider borderBottomWidth="3px" />
          <DrawerFooter pt={0} px={0}>
            <Box w="100%">
              <Box minH="90px" mx={6} py={4}>
                <InputLabel
                  labelName="Insurance Requirements"
                  toolTip="Select the requirement template to apply to the selected insureds. Defaults to the project's default requirements."
                />
                <RequirementTemplateDropdownMenu
                  companyId={companyId}
                  defaultTemplate={selectedTemplate}
                  dropdownId="requirement-template-dropdown-add-insureds-to-project-tray"
                  variant="primary"
                  onChange={setSelectedTemplate}
                  onUploadContract={onUploadContract}
                />
              </Box>
              <Tooltip
                isDisabled={selected.length}
                label="Select Insureds to Add to Project"
              >
                <Box>
                  <Button
                    data-analytics-id={analyticsIds.projectDetailsPage.addInsuredToProjectButton}
                    data-cy={CypressIds.createRecordTraySubmitButton}
                    dusk={JobProfilePage.addToProjectButton}
                    form="addMultipleInsureds"
                    isDisabled={!selected.length || currentlyAddingInsuredToProject}
                    mx={6}
                    type="submit"
                    width="90%"
                    onClick={handleAddInsuredsToProject}
                  >
                    {`Add ${selected.length ? selected.length : ''} to Project`}
                  </Button>
                </Box>
              </Tooltip>
            </Box>
          </DrawerFooter>
        </>
      );
    }

    return (
      <DrawerFooter>
        <Button
          data-analytics-id={analyticsIds.projectDetailsPage.addInsuredToListButton}
          data-cy={CypressIds.createRecordTrayCreateInsuredSubmitButton}
          dusk={JobProfilePage.addInsuredToListButton}
          form="createInsured"
          isDisabled={currentlyCreatingInsured}
          type="submit"
          variant="secondary"
          width="100%"
        >
          Add Insured to List
        </Button>
      </DrawerFooter>
    );
  };

  return (
    <>
      <DrawerBody px={0} py={0}>
        <Flex flexDirection="column" height="100%" justifyContent="space-between">
          <Box
            borderBottom={`3px solid ${colors.greyScale['200']}`}
            pb={8}
            px={6}
            width="100%"
          >
            <Text color="greyScale.500" textStyles="bodyMd">
              Adding insureds to a project will create a record. This is the first step in tracking
              insurance requirements for an insured.
            </Text>

            <Flex justifyContent="center">
              {Object.values(multiSelectInsuredsOptions).map(option => (
                <Box
                  borderBottom={currentTab === option.key ? `1px solid ${colors.primaryScale['600']}` : 'none'}
                  cursor="pointer"
                  data-analytics-id={option.dataAnalyticsId}
                  data-cy={option.cypressId}
                  dusk={option.dusk}
                  key={option.key}
                  mx={4}
                  py={4}
                  onClick={() => dispatch(updateCompanyInsuredsTab(option.key))}
                >
                  <Text
                    color={currentTab === option.key ? colors.primaryScale['600'] : colors.primaryScale['200']}
                    textStyle={currentTab === option.key ? 'bodyLg500' : 'bodyLg'}
                  >
                    {option.label}
                  </Text>
                </Box>
              ))}
            </Flex>
          </Box>

          <Box height="inherit" px={6}>
            {renderAddingComponent()}
          </Box>
        </Flex>
      </DrawerBody>

      {renderSubmitButton()}
    </>
  );
};

AddInsuredsToProjectTray.propTypes = { companyId: PropTypes.number.isRequired };

export default AddInsuredsToProjectTray;
